import React, { useState } from 'react';
import {
    Box, Typography, Card, CardContent, Grid, Table, TableBody,
    TableCell, TableContainer, TableHead, TableRow, Button, Chip,
    TextField, MenuItem, LinearProgress
} from '@mui/material';
import {
    Download as DownloadIcon, Print as PrintIcon,
    Assessment as AssessmentIcon, School as SchoolIcon,
    TrendingUp as TrendingUpIcon
} from '@mui/icons-material';
import { Bar } from 'react-chartjs-2';
import 'chart.js/auto';

const CumulativeReport = () => {
    const [examFilter, setExamFilter] = useState('');
    const [classFilter, setClassFilter] = useState('');

    const examResults = [
        { class: 'Class 10A', students: 45, appeared: 44, passed: 40, failed: 4, absent: 1, passRate: 91, avgMarks: 72, topScore: 96, grade: 'A' },
        { class: 'Class 10B', students: 42, appeared: 42, passed: 35, failed: 7, absent: 0, passRate: 83, avgMarks: 68, topScore: 94, grade: 'B+' },
        { class: 'Class 9A', students: 40, appeared: 39, passed: 36, failed: 3, absent: 1, passRate: 92, avgMarks: 75, topScore: 98, grade: 'A' },
        { class: 'Class 9B', students: 38, appeared: 37, passed: 29, failed: 8, absent: 1, passRate: 78, avgMarks: 65, topScore: 90, grade: 'B' },
        { class: 'Class 8A', students: 44, appeared: 43, passed: 41, failed: 2, absent: 1, passRate: 95, avgMarks: 78, topScore: 97, grade: 'A+' },
        { class: 'Class 7A', students: 40, appeared: 40, passed: 35, failed: 5, absent: 0, passRate: 88, avgMarks: 70, topScore: 92, grade: 'B+' },
        { class: 'Class 6A', students: 38, appeared: 38, passed: 34, failed: 4, absent: 0, passRate: 89, avgMarks: 71, topScore: 93, grade: 'A' },
        { class: 'Class 5A', students: 42, appeared: 41, passed: 38, failed: 3, absent: 1, passRate: 93, avgMarks: 76, topScore: 95, grade: 'A' },
    ];

    const chartData = {
        labels: examResults.map(r => r.class),
        datasets: [
            { label: 'Pass Rate %', data: examResults.map(r => r.passRate), backgroundColor: '#4caf50', borderRadius: 4, barThickness: 20 },
            { label: 'Avg Marks %', data: examResults.map(r => r.avgMarks), backgroundColor: '#2196f3', borderRadius: 4, barThickness: 20 },
        ],
    };

    const totalStudents = examResults.reduce((a, r) => a + r.students, 0);
    const totalAppeared = examResults.reduce((a, r) => a + r.appeared, 0);
    const totalPassed = examResults.reduce((a, r) => a + r.passed, 0);
    const overallPassRate = Math.round((totalPassed / totalAppeared) * 100);
    const overallAvg = Math.round(examResults.reduce((a, r) => a + r.avgMarks, 0) / examResults.length);

    const filteredResults = examResults.filter(r => !classFilter || r.class === classFilter);

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AssessmentIcon sx={{ color: '#1a9e8f', fontSize: 28 }} />
                    <Typography variant="h5" fontWeight={700}>Cumulative Report</Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <Button variant="outlined" startIcon={<PrintIcon />} size="small">Print</Button>
                    <Button variant="outlined" startIcon={<DownloadIcon />} size="small">Export</Button>
                </Box>
            </Box>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ background: 'linear-gradient(135deg, #1a9e8f, #26c6b8)' }}>
                        <CardContent sx={{ textAlign: 'center', color: '#fff' }}>
                            <Typography variant="body2" sx={{ opacity: 0.9 }}>Total Students</Typography>
                            <Typography variant="h3" fontWeight={700}>{totalStudents}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #4caf50' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Overall Pass Rate</Typography>
                            <Typography variant="h4" fontWeight={700} color="success.main">{overallPassRate}%</Typography>
                            <LinearProgress variant="determinate" value={overallPassRate}
                                sx={{ mt: 0.5, height: 4, borderRadius: 2 }} />
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #2196f3' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Average Marks</Typography>
                            <Typography variant="h4" fontWeight={700}>{overallAvg}%</Typography>
                        </CardContent>
                    </Card>
                </Grid>
                <Grid item xs={6} sm={3}>
                    <Card sx={{ borderLeft: '4px solid #ff9800' }}>
                        <CardContent sx={{ textAlign: 'center' }}>
                            <Typography variant="body2" color="text.secondary">Highest Score</Typography>
                            <Typography variant="h4" fontWeight={700}>{Math.max(...examResults.map(r => r.topScore))}</Typography>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid item xs={12}>
                    <Card>
                        <CardContent>
                            <Typography variant="subtitle2" fontWeight={700} gutterBottom>Class-wise Performance Comparison</Typography>
                            <Box height={250}>
                                <Bar data={chartData} options={{
                                    responsive: true, maintainAspectRatio: false,
                                    plugins: { legend: { position: 'top' } },
                                    scales: { y: { beginAtZero: true, max: 100, grid: { color: '#f0f0f0' } }, x: { grid: { display: false } } }
                                }} />
                            </Box>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>

            <Card>
                <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                        <Typography variant="subtitle2" fontWeight={700}>Detailed Results</Typography>
                        <Grid container spacing={1} sx={{ maxWidth: 300 }}>
                            <Grid item xs={6}>
                                <TextField fullWidth select size="small" label="Exam" value={examFilter} onChange={(e) => setExamFilter(e.target.value)}>
                                    <MenuItem value="">All Exams</MenuItem>
                                    {['Unit Test 1', 'Unit Test 2', 'Half Yearly', 'Annual'].map(e => <MenuItem key={e} value={e}>{e}</MenuItem>)}
                                </TextField>
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth select size="small" label="Class" value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
                                    <MenuItem value="">All</MenuItem>
                                    {examResults.map(r => <MenuItem key={r.class} value={r.class}>{r.class}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>
                    </Box>
                    <TableContainer>
                        <Table size="small">
                            <TableHead>
                                <TableRow sx={{ bgcolor: '#f5f5f5' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Class</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Appeared</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: '#4caf50' }}>Passed</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: '#e53935' }}>Failed</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Absent</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Pass %</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Avg Marks</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>Top</TableCell>
                                    <TableCell sx={{ fontWeight: 700 }}>Grade</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {filteredResults.map(r => (
                                    <TableRow key={r.class} hover>
                                        <TableCell sx={{ fontWeight: 600 }}>{r.class}</TableCell>
                                        <TableCell align="center">{r.students}</TableCell>
                                        <TableCell align="center">{r.appeared}</TableCell>
                                        <TableCell align="center" sx={{ color: '#4caf50', fontWeight: 600 }}>{r.passed}</TableCell>
                                        <TableCell align="center" sx={{ color: '#e53935', fontWeight: 600 }}>{r.failed}</TableCell>
                                        <TableCell align="center">{r.absent}</TableCell>
                                        <TableCell align="center">
                                            <Chip size="small" label={`${r.passRate}%`}
                                                color={r.passRate >= 90 ? 'success' : r.passRate >= 80 ? 'warning' : 'error'} />
                                        </TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 600 }}>{r.avgMarks}%</TableCell>
                                        <TableCell align="center" sx={{ fontWeight: 700 }}>{r.topScore}</TableCell>
                                        <TableCell><Chip size="small" label={r.grade} variant="outlined" /></TableCell>
                                    </TableRow>
                                ))}
                                <TableRow sx={{ bgcolor: '#e8f5e9' }}>
                                    <TableCell sx={{ fontWeight: 700 }}>Total</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{totalStudents}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{totalAppeared}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: '#4caf50' }}>{totalPassed}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700, color: '#e53935' }}>{totalAppeared - totalPassed}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{totalStudents - totalAppeared}</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{overallPassRate}%</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{overallAvg}%</TableCell>
                                    <TableCell align="center" sx={{ fontWeight: 700 }}>{Math.max(...examResults.map(r => r.topScore))}</TableCell>
                                    <TableCell></TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </CardContent>
            </Card>
        </Box>
    );
};

export default CumulativeReport;
